import React, { useCallback, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { BodyBlockV2Item, StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import { cn } from 'components/lib/utils';

import { templateStudioSchema } from '../../validation-schema';

import { TemplateStudioBundleForm } from './BlocksForms/Bundle';
import { TemplateStudioDailyBonusForm } from './BlocksForms/DailyBonus';
import { TemplateStudioPromotionForm } from './BlocksForms/Promotion';
import { TemplateStudioRewardCalendarForm } from './BlocksForms/RewardCalendar';
import { TemplateStudioRichTextForm } from './BlocksForms/RichText';
import { TemplateStudioRollingOffersForm } from './BlocksForms/RollingOffers';
import { TemplateStudioTriggeredPopupsForm } from './BlocksForms/TriggeredPopups';
import { DesignBlockMenuItem } from './Components/DesignBlockMenuItem';
import { FormsSeparator, MergeSeparator } from './Components/FormMenuSeparator';
import { SortableBlockItem } from './Components/SortableBlockItem';

const createMapBlockToFormComponent = (): Partial<
  Record<StoreBodyBlock, React.ReactNode>
> => ({
  [StoreBodyBlock.specialOffers]: <TemplateStudioPromotionForm />,
  [StoreBodyBlock.bundles]: <TemplateStudioBundleForm />,
  [StoreBodyBlock.rollingOffers]: <TemplateStudioRollingOffersForm />,
  [StoreBodyBlock.richText]: <TemplateStudioRichTextForm />,
  [StoreBodyBlock.dailyBonusBadge]: <TemplateStudioDailyBonusForm />,
  [StoreBodyBlock.rewardCalendar]: <TemplateStudioRewardCalendarForm />,
  [StoreBodyBlock.triggeredPopups]: <TemplateStudioTriggeredPopupsForm />
});

const getV2ItemId = (item: BodyBlockV2Item): string => {
  return item.blocks.map(String).join('+');
};

const MergedBlockGroupWrapper: React.FC<{
  children: React.ReactNode;
  dragHandleListeners?: any;
  dragHandleAttributes?: any;
  isAnyDragging?: boolean;
  isCurrentGroupDragged?: boolean;
}> = ({
  children,
  dragHandleListeners,
  dragHandleAttributes,
  isAnyDragging = false,
  isCurrentGroupDragged = false
}) => {
  return (
    <div className="group/mergedGroup relative bg-neutral-100 rounded-lg py-1 px-1">
      <span
        {...(dragHandleListeners || {})}
        {...(dragHandleAttributes || {})}
        className={cn(
          'absolute -left-1 top-1/2 -translate-y-1/2 z-10',
          'cursor-grab active:cursor-grabbing transition-opacity',
          'bg-white rounded-full shadow-sm border border-neutral-200',
          'px-0.5 py-3 flex items-center justify-center',
          isAnyDragging
            ? isCurrentGroupDragged
              ? 'opacity-100'
              : 'opacity-0'
            : 'opacity-0 group-hover/mergedGroup:opacity-100'
        )}
      >
        <FontAwesomeIcon
          icon="fa-solid fa-grip-dots-vertical"
          className="text-xs text-base-mutedForeground"
        />
      </span>
      {children}
    </div>
  );
};

export const TemplateStudioDraggableBlockForms: React.FC<{
  defaultBodyBlocksOrder: StoreBodyBlock[];
}> = ({ defaultBodyBlocksOrder }) => {
  const { resetSidebars } = useTemplateStudioState();
  const { control, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const bodyBlocksOrderV2 = useWatch({
    control,
    name: 'bodyBlocksOrderV2'
  });

  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    resetSidebars();
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = bodyBlocksOrderV2.findIndex(
        (item) => getV2ItemId(item) === String(active.id)
      );
      const newIndex = bodyBlocksOrderV2.findIndex(
        (item) => getV2ItemId(item) === String(over.id)
      );

      const reordered = arrayMove(bodyBlocksOrderV2, oldIndex, newIndex);

      setValue('bodyBlocksOrderV2', reordered, {
        shouldValidate: true,
        shouldDirty: true
      });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleMerge = useCallback(
    (index: number) => {
      const current = bodyBlocksOrderV2[index];
      const next = bodyBlocksOrderV2[index + 1];
      if (!current || !next) return;

      const mergedItem: BodyBlockV2Item = {
        blocks: [...current.blocks, ...next.blocks],
        isVisible: current.isVisible && next.isVisible,
        isMergeable: true
      };

      const updated = [
        ...bodyBlocksOrderV2.slice(0, index),
        mergedItem,
        ...bodyBlocksOrderV2.slice(index + 2)
      ];

      setValue('bodyBlocksOrderV2', updated, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [bodyBlocksOrderV2, setValue]
  );

  const handleDetach = useCallback(
    (v2ItemIndex: number, blockToDetach: StoreBodyBlock) => {
      const item = bodyBlocksOrderV2[v2ItemIndex];
      if (!item) return;

      const blocks = item.blocks;
      const blockIndex = blocks.indexOf(blockToDetach);
      const remainingBlocks = blocks.filter((b) => b !== blockToDetach);
      const placeAbove = blockIndex < Math.floor(blocks.length / 2);

      const updatedItem: BodyBlockV2Item = {
        blocks: remainingBlocks,
        isVisible: item.isVisible,
        isMergeable: true
      };

      const detachedItem: BodyBlockV2Item = {
        blocks: [blockToDetach],
        isVisible: item.isVisible,
        isMergeable: true
      };

      const updated = [
        ...bodyBlocksOrderV2.slice(0, v2ItemIndex),
        ...(placeAbove
          ? [detachedItem, updatedItem]
          : [updatedItem, detachedItem]),
        ...bodyBlocksOrderV2.slice(v2ItemIndex + 1)
      ];

      setValue('bodyBlocksOrderV2', updated, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [bodyBlocksOrderV2, setValue]
  );

  const mapBlockToFormComponent = createMapBlockToFormComponent();

  const items = bodyBlocksOrderV2.map(getV2ItemId);

  const activeItem = activeId
    ? bodyBlocksOrderV2.find((item) => getV2ItemId(item) === activeId)
    : null;

  const canMergeAdjacent = (index: number): boolean => {
    const current = bodyBlocksOrderV2[index];
    const next = bodyBlocksOrderV2[index + 1];
    return (
      !!current?.isMergeable &&
      !!next?.isMergeable &&
      current.isVisible &&
      next.isVisible
    );
  };

  return (
    <DndContext
      id="tb-draggable-blocks-container"
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {bodyBlocksOrderV2.map((v2Item, index) => {
          const itemId = getV2ItemId(v2Item);
          const isHidden = !v2Item.isVisible;
          const isItemDragged = activeId === itemId;
          const blocks = v2Item.blocks;
          const isMergedGroup = blocks.length > 1;
          const showMergeSeparator = canMergeAdjacent(index);

          const nextItem = bodyBlocksOrderV2[index + 1];
          const nextIsMergedGroup = nextItem && nextItem.blocks.length > 1;
          const hideSeparator =
            isMergedGroup || nextIsMergedGroup || showMergeSeparator;

          if (isMergedGroup) {
            return (
              <React.Fragment key={itemId}>
                <SortableBlockItem
                  className="tb-sortable-block-item"
                  id={itemId}
                  isHidden={isHidden}
                >
                  <MergedBlockGroupWrapper
                    isAnyDragging={activeId !== null}
                    isCurrentGroupDragged={isItemDragged}
                  >
                    {blocks.map((block) => (
                      <DesignBlockMenuItem
                        key={block}
                        className="design-block-menu-item"
                        blockType={block}
                        isHidden={isHidden}
                        isDragging={activeId !== null}
                        isCurrentItemDragged={isItemDragged}
                        isMergedSubBlock={true}
                        onDetach={() => handleDetach(index, block)}
                      >
                        {mapBlockToFormComponent[block]}
                      </DesignBlockMenuItem>
                    ))}
                  </MergedBlockGroupWrapper>
                </SortableBlockItem>
                {showMergeSeparator && (
                  <MergeSeparator onMerge={() => handleMerge(index)} />
                )}
              </React.Fragment>
            );
          }

          const primaryBlock = blocks[0];
          const formComponent = mapBlockToFormComponent[primaryBlock];

          return (
            <React.Fragment key={itemId}>
              <SortableBlockItem
                className="tb-sortable-block-item"
                id={itemId}
                isHidden={isHidden}
              >
                <DesignBlockMenuItem
                  className="design-block-menu-item"
                  blockType={primaryBlock}
                  isHidden={isHidden}
                  isDragging={activeId !== null}
                  isCurrentItemDragged={isItemDragged}
                >
                  {formComponent}
                </DesignBlockMenuItem>
              </SortableBlockItem>
              {showMergeSeparator ? (
                <MergeSeparator onMerge={() => handleMerge(index)} />
              ) : (
                !hideSeparator && <FormsSeparator />
              )}
            </React.Fragment>
          );
        })}
      </SortableContext>
      <DragOverlay>
        {activeId && activeItem ? (
          <div className="cursor-grabbing shadow-lg bg-white z-50">
            {activeItem.blocks.length > 1 ? (
              <MergedBlockGroupWrapper
                isAnyDragging={true}
                isCurrentGroupDragged={true}
              >
                {activeItem.blocks.map((block) => (
                  <DesignBlockMenuItem
                    key={block}
                    blockType={block}
                    isHidden={!activeItem.isVisible}
                    isDragging={true}
                    isCurrentItemDragged={true}
                    isMergedSubBlock={true}
                  >
                    {mapBlockToFormComponent[block]}
                  </DesignBlockMenuItem>
                ))}
              </MergedBlockGroupWrapper>
            ) : (
              <DesignBlockMenuItem
                blockType={activeItem.blocks[0]}
                isHidden={!activeItem.isVisible}
                isDragging={true}
                isCurrentItemDragged={true}
              >
                {mapBlockToFormComponent[activeItem.blocks[0]]}
              </DesignBlockMenuItem>
            )}
            <FormsSeparator />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
